package com.ijoin.data.mapper

import com.ijoin.data.model.Event
import com.ijoin.data.model.EventWizardRequest
import com.ijoin.platform.createTimeZone
import com.ijoin.platform.displayId
import com.ijoin.platform.getLocalCurrency
import com.ijoin.platform.getLocalTimeZone
import com.ijoin.ui.screens.EventWizardData
import com.ijoin.ui.screens.ScheduleType
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

fun EventWizardData.toRequest(): EventWizardRequest {
    val normalizedAddress = locationAddress.ifBlank { "" }
    val (images, videos) = mediaUrls.partition { url ->
        url.startsWith("data:image", ignoreCase = true) || url.endsWith(".jpg", true) ||
                url.endsWith(".jpeg", true) || url.endsWith(".png", true) || url.endsWith(".gif", true)
    }
    val cleanedVideos = videos.filter { it.startsWith("data:video", ignoreCase = true) || it.endsWith(".mp4", true) || it.endsWith(".mov", true) || it.endsWith(".webm", true) }
    val leftover = videos - cleanedVideos.toSet()
    val photoUrls = images + leftover
    val mobilePhotoUrls = mobileImageUrls.filterNot { it.isBlank() }

    return EventWizardRequest(
        title = title,
        description = description,
        photoDataUrls = photoUrls,
        mobilePhotoDataUrls = mobilePhotoUrls,
        videoDataUrls = cleanedVideos,
        mediaStorageFolder = mediaStorageFolder,
        status = status,
        maxCapacity = maxCapacity,
        ticketPrice = ticketPrice,
        currency = currency,
        hiddenInfo = hiddenInfo,
        scheduleType = scheduleType?.name,
        eventDate = eventDate?.toString(),
        eventEndDate = eventEndDate?.toString(),
        eventTime = eventTime,
        eventEndTime = eventEndTime.takeIf { it.isNotBlank() },
        timeZone = timeZone,
        selectedDaysOfWeek = selectedDaysOfWeek.toList().sorted(),
        weeklyTime = weeklyTime,
        weeklyEndTime = weeklyEndTime.takeIf { it.isNotBlank() },
        selectedDaysOfMonth = selectedDaysOfMonth.toList().sorted(),
        monthlyTime = monthlyTime,
        slotDaysOfWeek = slotDaysOfWeek.toList().sorted(),
        slotStartTime = slotStartTime,
        slotEndTime = slotEndTime,
        minSlotsPerBooking = minSlotsPerBooking,
        slotDurationMinutes = slotDurationMinutes,
        slotDayConfigurations = slotDayTimes
            .filterKeys { slotDaysOfWeek.contains(it) }
            .takeIf { it.isNotEmpty() }
            ?.map { (dayOfWeek, times) ->
                EventWizardRequest.DaySlotConfiguration(
                    dayOfWeek = dayOfWeek,
                    startTime = times.first,
                    endTime = times.second
                )
            }?.sortedBy { it.dayOfWeek },
        online = isOnline,
        locationName = normalizedAddress,
        locationAddress = normalizedAddress,
        locationCity = "",
        locationCountry = "",
        locationLatitude = locationLatitude,
        locationLongitude = locationLongitude,
        onlineLink = onlineLink,
        enableCarpool = enableCarpool,
        carpoolPaymentPerPerson = carpoolPaymentPerPerson,
        enableGroupChat = enableGroupChat,
        requireContactInfo = requireContactInfo,
        useAiLanding = useAiLanding,
        landingDescription = landingDescription.takeIf { it.isNotBlank() },
        enableLandingPage = enableLandingPage
    )
}

fun Event.toWizardData(): EventWizardData {
    val resolvedSchedule = scheduleType?.let { type ->
        runCatching { ScheduleType.valueOf(type.uppercase()) }.getOrNull()
    }
    val eventZone = if (!timeZone.isNullOrBlank()) createTimeZone(timeZone) else TimeZone.UTC
    val resolvedDate = parseEventDate(eventDate, eventZone)
    val resolvedTime = resolveEventTime(eventZone)
    val resolvedEndDate = parseEventDate(eventEndDate, eventZone) ?: resolvedDate
    val resolvedEndTime = resolveEventEndTime(eventZone)
    val resolvedCurrency = currency ?: getLocalCurrency().code
    val resolvedTimeZone = timeZone ?: getLocalTimeZone().displayId
    val resolvedMedia = mediaUrls?.filterNot { it.isBlank() } ?: buildList {
        coverImageUrl?.takeIf { it.isNotBlank() }?.let { add(it) }
        imageUrls?.filterNot { it.isBlank() }?.let { addAll(it) }
        videoUrls?.filterNot { it.isBlank() }?.let { addAll(it) }
    }
    val resolvedMobileMedia = mobileImageUrls?.filterNot { it.isBlank() } ?: emptyList()
    val resolvedLocationAddress = listOf(
        locationAddress,
        locationName,
        locationCity,
        locationCountry
    ).firstOrNull { !it.isNullOrBlank() }.orEmpty()
    val slotTimes = slotDayConfigurations?.associate { config ->
        config.dayOfWeek to (config.startTime to config.endTime)
    } ?: emptyMap()

    return EventWizardData(
        title = title,
        description = description.orEmpty(),
        mediaUrls = resolvedMedia.distinct(),
        mobileImageUrls = resolvedMobileMedia,
        mediaStorageFolder = mediaStorageFolder,
        maxCapacity = maxCapacity,
        ticketPrice = ticketPrice ?: 0.0,
        currency = resolvedCurrency,
        hiddenInfo = hiddenInfo.orEmpty(),
        status = status?.name,
        scheduleType = resolvedSchedule,
        eventDate = resolvedDate,
        eventEndDate = resolvedEndDate,
        eventTime = resolvedTime,
        eventEndTime = resolvedEndTime,
        timeZone = resolvedTimeZone,
        selectedDaysOfWeek = selectedDaysOfWeek?.toSet() ?: emptySet(),
        weeklyTime = weeklyTime.orEmpty(),
        weeklyEndTime = calculateEndTime(weeklyTime, durationMinutes),
        selectedDaysOfMonth = selectedDaysOfMonth?.toSet() ?: emptySet(),
        monthlyTime = monthlyTime.orEmpty(),
        slotDaysOfWeek = slotDayConfigurations?.map { it.dayOfWeek }?.toSet() ?: emptySet(),
        slotStartTime = slotDayConfigurations?.firstOrNull()?.startTime.orEmpty(),
        slotEndTime = slotDayConfigurations?.firstOrNull()?.endTime.orEmpty(),
        minSlotsPerBooking = minSlotsPerBooking,
        slotDurationMinutes = slotDurationMinutes,
        slotDayTimes = slotTimes,
        isOnline = isOnline == true,
        locationAddress = resolvedLocationAddress,
        locationLatitude = locationLatitude,
        locationLongitude = locationLongitude,
        onlineLink = onlineLink.orEmpty(),
        enableCarpool = carpoolEnabled == true,
        carpoolPaymentPerPerson = carpoolPaymentPerPerson,
        enableGroupChat = groupChatEnabled == true,
        requireContactInfo = requireContactInfo == true
    )
}

private fun parseEventDate(instant: Instant?, zone: TimeZone): LocalDate? {
    if (instant == null) return null
    return try {
        instant.toLocalDateTime(zone).date
    } catch (e: Exception) {
        null
    }
}

private fun Event.resolveEventTime(zone: TimeZone): String {
    if (!eventTime.isNullOrBlank()) {
        return eventTime!!
    }
    val instant = eventDate ?: return ""
    return try {
        val ldt = instant.toLocalDateTime(zone)
        "${ldt.hour.toString().padStart(2, '0')}:${ldt.minute.toString().padStart(2, '0')}"
    } catch (e: Exception) {
        ""
    }
}

private fun Event.resolveEventEndTime(zone: TimeZone): String {
    val instant = eventEndDate ?: return ""
    return try {
        val ldt = instant.toLocalDateTime(zone)
        "${ldt.hour.toString().padStart(2, '0')}:${ldt.minute.toString().padStart(2, '0')}"
    } catch (e: Exception) {
        ""
    }
}

/**
 * Calculates end time from start time and duration
 * @param startTime Start time in "HH:mm" format
 * @param durationMinutes Duration in minutes
 * @return End time in "HH:mm" format, or empty string if calculation fails
 */
private fun calculateEndTime(startTime: String?, durationMinutes: Int?): String {
    if (startTime.isNullOrBlank() || durationMinutes == null || durationMinutes <= 0) return ""
    return try {
        val parts = startTime.split(":")
        if (parts.size < 2) return ""
        val startHour = parts[0].toIntOrNull() ?: return ""
        val startMinute = parts[1].toIntOrNull() ?: return ""
        val totalMinutes = startHour * 60 + startMinute + durationMinutes
        val endHour = (totalMinutes / 60) % 24
        val endMinute = totalMinutes % 60
        "${endHour.toString().padStart(2, '0')}:${endMinute.toString().padStart(2, '0')}"
    } catch (e: Exception) {
        ""
    }
}
